import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import type { WhatsAppTemplate, UseWhatsAppTemplatesResult } from '@/types/whatsapp';

export function useWhatsAppTemplates(businessId: string | null): UseWhatsAppTemplatesResult {
  const queryClient = useQueryClient();

  const queryKey = ['whatsapp-templates', businessId];

  const { data: templates = [], isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!businessId) return [];

      const { data, error } = await supabase
        .from('whatsapp_templates')
        .select('*')
        .eq('business_id', businessId)
        .order('name', { ascending: true });

      if (error) throw error;
      return (data || []) as WhatsAppTemplate[];
    },
    enabled: !!businessId,
  });

  const approvedTemplates = templates.filter(t => t.status === 'APPROVED');

  const syncMutation = useMutation({
    mutationFn: async () => {
      if (!businessId) throw new Error('No business selected');

      const response = await supabase.functions.invoke('whatsapp-setup', {
        body: {
          action: 'sync_templates',
          business_id: businessId,
        },
      });

      if (response.error) throw response.error;
      if (response.data?.error) throw new Error(response.data.error);

      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Templates synced', {
        description: `${data.templates_synced} templates updated from Meta`,
      });
    },
    onError: (error: Error) => {
      toast.error('Failed to sync templates', {
        description: error.message,
      });
    },
  });

  const syncTemplates = useCallback(async () => {
    await syncMutation.mutateAsync();
  }, [syncMutation]);

  return {
    templates,
    isLoading,
    error: error as Error | null,
    approvedTemplates,
    syncTemplates,
    refresh: refetch,
  };
}
